import {
  CampaignAutomationStatusEnum,
  CampaignsBulkStatusEnum,
} from '@/types';

type StyleToken = {
  background: string;
  color: string;
  border?: string;
  dot?: string;
};

type CssVars = Record<string, string>;

const DEFAULT_STATUS_STYLE: StyleToken = {
  background: 'var(--surface-200, #f3f4f6)',
  color: 'var(--text-color, #111827)',
  border: 'rgba(17, 24, 39, 0.08)',
};

const STATUS_COLOR_MAP: Record<string, StyleToken> = {
  [CampaignAutomationStatusEnum.ACTIVE]: {
    background: 'rgba(34, 197, 94, 0.15)',
    color: '#166534',
    border: 'rgba(34, 197, 94, 0.35)',
  },
  [CampaignAutomationStatusEnum.DISABLED]: {
    background: 'rgba(148, 163, 184, 0.16)',
    color: '#475569',
    border: 'rgba(148, 163, 184, 0.38)',
  },
  [CampaignAutomationStatusEnum.DRAFT]: {
    background: 'rgba(59, 130, 246, 0.14)',
    color: '#1d4ed8',
    border: 'rgba(59, 130, 246, 0.36)',
  },
  [CampaignAutomationStatusEnum.ARCHIVED]: {
    background: 'rgba(100, 116, 139, 0.16)',
    color: '#475569',
    border: 'rgba(100, 116, 139, 0.32)',
  },
  [CampaignAutomationStatusEnum.DELETED]: {
    background: 'rgba(248, 113, 113, 0.16)',
    color: '#b91c1c',
    border: 'rgba(248, 113, 113, 0.35)',
  },
  [CampaignsBulkStatusEnum.DRAFT]: {
    background: 'rgba(129, 140, 248, 0.16)',
    color: '#4338ca',
    border: 'rgba(129, 140, 248, 0.34)',
  },
  [CampaignsBulkStatusEnum.BEING_EDITED]: {
    background: 'rgba(96, 165, 250, 0.16)',
    color: '#1d4ed8',
    border: 'rgba(59, 130, 246, 0.34)',
  },
  [CampaignsBulkStatusEnum.SCHEDULED]: {
    background: 'rgba(245, 158, 11, 0.18)',
    color: '#b45309',
    border: 'rgba(245, 158, 11, 0.4)',
  },
  [CampaignsBulkStatusEnum.SENDING]: {
    background: 'rgba(14, 165, 233, 0.16)',
    color: '#0369a1',
    border: 'rgba(14, 165, 233, 0.35)',
  },
  [CampaignsBulkStatusEnum.SENT]: {
    background: 'rgba(16, 185, 129, 0.16)',
    color: '#047857',
    border: 'rgba(16, 185, 129, 0.35)',
  },
  [CampaignsBulkStatusEnum.SUSPENDED]: {
    background: 'rgba(251, 191, 36, 0.18)',
    color: '#92400e',
    border: 'rgba(251, 191, 36, 0.4)',
  },
  [CampaignsBulkStatusEnum.SUSPENDED_BY_USER]: {
    background: 'rgba(249, 115, 22, 0.16)',
    color: '#c2410c',
    border: 'rgba(249, 115, 22, 0.35)',
  },
  [CampaignsBulkStatusEnum.ARCHIVED_BY_USER]: {
    background: 'rgba(148, 163, 184, 0.18)',
    color: '#475569',
    border: 'rgba(148, 163, 184, 0.38)',
  },
  [CampaignsBulkStatusEnum.ARCHIVED]: {
    background: 'rgba(100, 116, 139, 0.18)',
    color: '#475569',
    border: 'rgba(100, 116, 139, 0.32)',
  },
  [CampaignsBulkStatusEnum.DELETED]: {
    background: 'rgba(248, 113, 113, 0.16)',
    color: '#b91c1c',
    border: 'rgba(248, 113, 113, 0.35)',
  },
};

const DEFAULT_CHANNEL_STYLE: StyleToken = {
  background: 'rgba(148, 163, 184, 0.12)',
  color: '#334155',
  border: 'rgba(148, 163, 184, 0.28)',
};

const CHANNEL_COLOR_MAP: Record<string, StyleToken> = {
  email: {
    background: 'rgba(59, 130, 246, 0.14)',
    color: '#1d4ed8',
    border: 'rgba(59, 130, 246, 0.32)',
  },
  sms: {
    background: 'rgba(16, 185, 129, 0.14)',
    color: '#047857',
    border: 'rgba(16, 185, 129, 0.32)',
  },
  pushnotifications: {
    background: 'rgba(245, 158, 11, 0.16)',
    color: '#b45309',
    border: 'rgba(245, 158, 11, 0.36)',
  },
  whatsapp: {
    background: 'rgba(52, 211, 153, 0.14)',
    color: '#0f766e',
    border: 'rgba(52, 211, 153, 0.32)',
  },
  facebookmessenger: {
    background: 'rgba(96, 165, 250, 0.16)',
    color: '#1d4ed8',
    border: 'rgba(96, 165, 250, 0.34)',
  },
  popup: {
    background: 'rgba(244, 114, 182, 0.18)',
    color: '#be185d',
    border: 'rgba(244, 114, 182, 0.38)',
  },
  embed: {
    background: 'rgba(129, 140, 248, 0.18)',
    color: '#4338ca',
    border: 'rgba(129, 140, 248, 0.36)',
  },
  rcs: {
    background: 'rgba(167, 139, 250, 0.18)',
    color: '#6d28d9',
    border: 'rgba(167, 139, 250, 0.38)',
  },
  richtexting: {
    background: 'rgba(167, 139, 250, 0.18)',
    color: '#6d28d9',
    border: 'rgba(167, 139, 250, 0.38)',
  },
  display: {
    background: 'rgba(96, 165, 250, 0.12)',
    color: '#1d4ed8',
    border: 'rgba(96, 165, 250, 0.28)',
  },
};

const CHANNEL_ICON_MAP: Record<string, string> = {
  email: 'fa-regular fa-envelope',
  sms: 'fa-regular fa-sms',
  pushnotifications: 'fa-regular fa-bell',
  whatsapp: 'fa-brands fa-whatsapp',
  facebookmessenger: 'fa-brands fa-facebook-messenger',
  popup: 'fa-regular fa-share',
  embed: 'fa-regular fa-list-alt',
  rcs: 'fa-regular fa-message-smile',
  richtexting: 'fa-regular fa-message-smile',
  display: 'fa-regular fa-desktop',
};

const CHANNEL_ALIASES: Record<string, string> = {
  boxsendemail: 'email',
  boxsendsms: 'sms',
  boxsendwhatsapp: 'whatsapp',
  boxsendfbmessenger: 'facebookmessenger',
  boxsendpushnotification: 'pushnotifications',
  boxdisplayfreepopup: 'popup',
  boxdisplayfreemodel: 'embed',
  boxsendrcs: 'rcs',
  smartcontent: 'embed',
  'smart-content': 'embed',
  smart_content: 'embed',
  popin: 'popup',
  'pop-in': 'popup',
};

const STATUS_ICON_MAP: Record<string, string> = {
  [CampaignAutomationStatusEnum.ACTIVE]: 'fa-regular fa-circle-check',
  [CampaignAutomationStatusEnum.DISABLED]: 'fa-regular fa-circle-minus',
  [CampaignAutomationStatusEnum.DRAFT]: 'fa-regular fa-pen-to-square',
  [CampaignAutomationStatusEnum.ARCHIVED]: 'fa-regular fa-box-archive',
  [CampaignAutomationStatusEnum.DELETED]: 'fa-regular fa-trash',
  [CampaignsBulkStatusEnum.DRAFT]: 'fa-regular fa-pen-to-square',
  [CampaignsBulkStatusEnum.BEING_EDITED]: 'fa-regular fa-pen-to-square',
  [CampaignsBulkStatusEnum.SCHEDULED]: 'fa-regular fa-calendar-check',
  [CampaignsBulkStatusEnum.SENDING]: 'fa-regular fa-paper-plane',
  [CampaignsBulkStatusEnum.SENT]: 'fa-regular fa-circle-check',
  [CampaignsBulkStatusEnum.SUSPENDED]: 'fa-regular fa-circle-pause',
  [CampaignsBulkStatusEnum.SUSPENDED_BY_USER]: 'fa-regular fa-user-slash',
  [CampaignsBulkStatusEnum.ARCHIVED_BY_USER]: 'fa-regular fa-box-archive',
  [CampaignsBulkStatusEnum.ARCHIVED]: 'fa-regular fa-box-archive',
  [CampaignsBulkStatusEnum.DELETED]: 'fa-regular fa-trash',
};

const buildCssVariables = (style: StyleToken, prefix: string): CssVars => {
  const vars: CssVars = {
    [`--${prefix}-bg`]: style.background,
    [`--${prefix}-color`]: style.color,
  };

  if (style.border) {
    vars[`--${prefix}-border`] = style.border;
  }

  if (style.dot) {
    vars[`--${prefix}-dot`] = style.dot;
  }

  return vars;
};

const normaliseKey = (value?: string | null): string => {
  if (!value) {
    return '';
  }

  return value.toString().trim().toLowerCase();
};

export const getStatusStyleVars = (status?: string | null): CssVars => {
  const key = normaliseKey(status);
  const style = (key && STATUS_COLOR_MAP[key]) || DEFAULT_STATUS_STYLE;

  return buildCssVariables({
    ...style,
    dot: style.dot ?? style.color,
  }, 'status');
};

export const getStatusIcon = (status?: string | null): string => {
  const key = normaliseKey(status);

  return (key && STATUS_ICON_MAP[key]) || '';
};

export const getChannelTagStyleVars = (channel?: string | null): CssVars => {
  const key = normaliseKey(channel);
  const resolved = key ? CHANNEL_ALIASES[key] ?? key : '';
  const style = (resolved && CHANNEL_COLOR_MAP[resolved]) || DEFAULT_CHANNEL_STYLE;

  return buildCssVariables({
    ...style,
    dot: style.dot ?? style.color,
  }, 'channel');
};

export const getChannelIcon = (channel?: string | null): string => {
  const key = normaliseKey(channel);
  const resolved = key ? CHANNEL_ALIASES[key] ?? key : '';

  return (resolved && CHANNEL_ICON_MAP[resolved]) || '';
};

export const getChannelCardStyleVars = (channel?: string | null): CssVars => {
  const vars = getChannelTagStyleVars(channel);

  const accent = vars['--channel-color'];
  const border = vars['--channel-border'];

  return {
    '--channel-accent': accent,
    '--channel-accent-border': border ?? accent,
  };
};
